import { v4 as uuid } from 'uuid'
import { QueryOptionFilter } from '@datasets/dataset-query-context/utils/queryOptions'
import { ColumnDataType, GovernanceType } from '@global/types'
import { UserOptions } from './types'
import { DbColumnName } from '../readGridToolbar/components/toggleColumns/types'

export function getColumnDataType(
  governanceType: GovernanceType,
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  fieldId: DbColumnName | string | number,
): ColumnDataType {
  const dbColumnName = fieldId as DbColumnName
  const isRowProperty = governanceType === 'property'

  const isSubmissionProperty = isRowProperty && dbColumnName === 'properties.submissionId'

  if (isSubmissionProperty) return ColumnDataType.SUBMISSION

  const isUserProperty =
    isRowProperty &&
    (dbColumnName === 'properties.createdById' || dbColumnName === 'properties.updatedById')

  if (isUserProperty) return ColumnDataType.USER

  // We currently do not support date governance types because we store dates as text in MYSQL
  // Row properties, however, use a date datatype
  const isDateProperty =
    governanceType === 'date' ||
    (isRowProperty &&
      (dbColumnName === 'properties.createdAt' || dbColumnName === 'properties.updatedAt'))

  if (isDateProperty) return ColumnDataType.DATE

  return ColumnDataType.DEFAULT
}

/**
 * Generates a filter for user fields based on search term.
 * This was created to support text searches on fields that depend on user IDs.
 * e.g. 'properties.updatedById' or 'properties.createdById'. Those fields store user IDs,
 * but we want to support text searches on the user's name.
 *
 * @param search Search term to match user names
 * @param fieldId 'properties.updatedById' | 'properties.createdById'
 * @param userOptions Object containing user data
 * @returns Array of filter objects
 *
 * @example
 * const filters = generateUserFilter('Doe', 'properties.updatedById', userOptions)
 * console.log(filters)
 * [
 *   {
 *     fieldId: 'properties.updatedById',
 *     id: 'test-uuid-123',
 *     operator: '_in',
 *     value: [
 *       {
 *         id: 123,
 *         name: 'John Doe'
 *       }
 *   }
 * ]
 */
export const generateUserFilter = (
  search: string,
  fieldId: 'properties.updatedById' | 'properties.createdById',
  userOptions: UserOptions | null,
): QueryOptionFilter[] => {
  if (!search || !userOptions) {
    return []
  }

  const searchLower = search.toLowerCase()

  // Get the property name from the fieldId (e.g. 'properties.updatedById' -> 'updatedById' -> 'updatedBy')
  // eslint-disable-next-line lingui/no-unlocalized-strings
  const fieldProperty = fieldId.split('.').pop()?.replace('Id', '') as keyof UserOptions

  // Get users from the specified field (either createdBy or updatedBy)
  const users = userOptions[fieldProperty]

  const matchingUsers = users.filter(user => user.name.toLowerCase().includes(searchLower))

  if (matchingUsers.length === 0) {
    return []
  }

  const filter = new QueryOptionFilter(String(fieldId))
  filter.id = uuid()
  filter.operator = '_in'
  filter.value = matchingUsers

  return [filter]
}
